import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useAudioRecorder } from '../../hooks/useAudioRecorder'
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition'
import { supabase } from '../../lib/supabase'
import { transcribeAudio } from '../../lib/ai'
import { CheckCircle, Circle, Trash2, Plus, Clock, Mic, Square, Pause, Play, Loader2, ChevronDown, ChevronUp, Search, Edit2 } from 'lucide-react'
import { Header } from '../Layout/Header'

// ─── Types ──────────────────────────────────────────────────────────

interface Task {
  id: string
  task: string
  completed: boolean
  scheduled_at?: string
  created_at: string
  created_by: string
  created_by_name: string
  assigned_to?: string
  assigned_to_name?: string
  completed_at?: string
  priority?: string
}

interface Rapportage {
  id: string
  user_id: string
  user_name: string
  title: string
  audio_url?: string
  duration_seconds: number
  notes: string
  created_at: string
  transcription?: string
  ai_summary?: Record<string, any> | null
  client_name?: string
}

// ─── Helpers ────────────────────────────────────────────────────────

function getISOWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7))
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7)
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0')
  const s = (seconds % 60).toString().padStart(2, '0')
  return `${m}:${s}`
}

function StemmingBadge({ stemming }: { stemming: string }) {
  const colors: Record<string, string> = {
    positief: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    neutraal: 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-200',
    negatief: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors[stemming] || colors.neutraal}`}>
      {stemming}
    </span>
  )
}

// ─── Component ──────────────────────────────────────────────────────

export const SalesDashboard: React.FC = () => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const recorder = useAudioRecorder()
  const speech = useSpeechRecognition()

  // Clock
  const [now, setNow] = useState(new Date())

  // Tasks
  const [tasks, setTasks] = useState<Task[]>([])
  const [showTaskForm, setShowTaskForm] = useState(false)
  const [newTask, setNewTask] = useState('')
  const [scheduledTime, setScheduledTime] = useState('')

  // Input mode: record via microphone or type manually
  const [inputMode, setInputMode] = useState<'record' | 'type'>('record')

  // Rapportages
  const [rapportages, setRapportages] = useState<Rapportage[]>([])
  const [rapTitle, setRapTitle] = useState('')
  const [rapNotes, setRapNotes] = useState('')
  const [rapClientName, setRapClientName] = useState('')
  const [rapTranscription, setRapTranscription] = useState('')
  const [saving, setSaving] = useState(false)
  const [successMsg, setSuccessMsg] = useState('')
  const [saveError, setSaveError] = useState<string | null>(null)
  const [playingId, setPlayingId] = useState<string | null>(null)
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null)

  // Whisper transcription state
  const [transcribing, setTranscribing] = useState(false)
  const [transcribeError, setTranscribeError] = useState<string | null>(null)

  // Rapportage list expand
  const [expandedRapId, setExpandedRapId] = useState<string | null>(null)

  // Search/filter for rapportages list
  const [rapSearch, setRapSearch] = useState('')
  const [rapFilterStemming, setRapFilterStemming] = useState<'all'|'positief'|'neutraal'|'negatief'>('all')

  // Edit rapportage
  const [editingRap, setEditingRap] = useState<Rapportage | null>(null)
  const [editForm, setEditForm] = useState({ title: '', client_name: '', notes: '' })
  const [editSaving, setEditSaving] = useState(false)

  // Scroll ref for transcription area
  const transcriptScrollRef = useRef<HTMLDivElement>(null)

  // ─── Clock tick ─────────────────────────────────────────────────

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(interval)
  }, [])

  // ─── Auto-scroll transcription during recording ─────────────────

  useEffect(() => {
    if (transcriptScrollRef.current && (recorder.status === 'recording' || recorder.status === 'paused')) {
      transcriptScrollRef.current.scrollTop = transcriptScrollRef.current.scrollHeight
    }
  }, [speech.transcript, speech.interimResult, recorder.status])

  // ─── Auto-transcribe + auto-generate AI on stop ──────────────────

  const prevStatusRef = useRef(recorder.status)

  useEffect(() => {
    const wasRecording = prevStatusRef.current === 'recording' || prevStatusRef.current === 'paused'
    prevStatusRef.current = recorder.status

    if (recorder.status === 'stopped' && wasRecording) {
      // Populate from browser speech recognition (may be empty on unsupported browsers)
      setRapTranscription(speech.transcript)
      // Always run Whisper for accurate server-side transcription
      if (recorder.audioBlob) {
        runWhisperOnBlob(recorder.audioBlob)
      }
    }
  }, [recorder.status])

  // ─── Tasks: load + realtime ─────────────────────────────────────

  useEffect(() => {
    loadTasks()

    const subscription = supabase
      .channel('sales-tasks')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks' }, () => {
        loadTasks()
      })
      .subscribe()

    return () => {
      subscription.unsubscribe()
    }
  }, [user?.id])

  const loadTasks = async () => {
    if (!user?.id) return
    const { data, error } = await supabase
      .from('tasks')
      .select('*')
      .or(`assigned_to.eq.${user.id},created_by.eq.${user.id}`)
      .order('completed', { ascending: true })
      .order('created_at', { ascending: false })

    if (!error) setTasks(data || [])
  }

  const addTask = async () => {
    if (!newTask.trim()) return
    const { error } = await supabase.from('tasks').insert([{
      task: newTask,
      completed: false,
      scheduled_at: scheduledTime ? new Date(`${new Date().toDateString()} ${scheduledTime}`).toISOString() : null,
      created_by: user?.id,
      created_by_name: user?.name || 'Onbekend',
      assigned_to: user?.id,
      assigned_to_name: user?.name,
      priority: 'normaal',
    }])
    if (!error) {
      setNewTask('')
      setScheduledTime('')
      setShowTaskForm(false)
      loadTasks()
    }
  }

  const toggleTask = async (taskId: string, current: boolean) => {
    const { error } = await supabase
      .from('tasks')
      .update({ completed: !current, completed_at: !current ? new Date().toISOString() : null })
      .eq('id', taskId)
    if (!error) loadTasks()
  }

  const deleteTask = async (taskId: string) => {
    const { error } = await supabase.from('tasks').delete().eq('id', taskId)
    if (!error) loadTasks()
  }

  // ─── Rapportages: load ──────────────────────────────────────────

  useEffect(() => {
    loadRapportages()
  }, [user?.id])

  const loadRapportages = async () => {
    if (!user?.id) return
    const { data, error } = await supabase
      .from('sales_rapportages')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(20)

    if (!error) setRapportages(data || [])
  }

  // ─── Recording: start/stop with speech ──────────────────────────

  const handleStartRecording = async () => {
    setTranscribeError(null)
    setRapTranscription('')
    setRapTitle('')
    setRapNotes('')
    setRapClientName('')
    setSaveError(null)
    speech.resetTranscript()

    await recorder.startRecording()
    if (speech.isSupported) {
      speech.startListening()
    }
  }

  const handleStopRecording = () => {
    recorder.stopRecording()
    speech.stopListening()
  }

  const runWhisperOnBlob = async (blob: Blob) => {
    setTranscribing(true)
    setTranscribeError(null)
    try {
      const text = await transcribeAudio(blob)
      if (text) setRapTranscription(text)
    } catch (err) {
      setTranscribeError(err instanceof Error ? err.message : 'Transcriptie mislukt')
    } finally {
      setTranscribing(false)
    }
  }

  const handleResetRecording = () => {
    recorder.resetRecording()
    speech.resetTranscript()
    setRapTitle('')
    setRapNotes('')
    setRapClientName('')
    setRapTranscription('')
    setSaveError(null)
  }

  // ─── Input mode switch ──────────────────────────────────────────

  const handleSwitchMode = (mode: 'record' | 'type') => {
    setInputMode(mode)
    handleResetRecording()
  }



  // ─── Rapportage: save ──────────────────────────────────────────

  const saveRapportage = async () => {
    if (!user) return
    if (inputMode === 'record' && !recorder.audioBlob) return

    setSaving(true)
    setSaveError(null)

    try {
      let audioUrl = ''

      if (inputMode === 'record' && recorder.audioBlob) {
        const filePath = `${user.id}/${Date.now()}.webm`
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('sales-audio')
          .upload(filePath, recorder.audioBlob)

        if (uploadError) throw uploadError

        // De server geeft het bestand een eigen naam; daar moet de URL op wijzen.
        const { data: publicUrlData } = supabase.storage
          .from('sales-audio')
          .getPublicUrl(uploadData?.path || filePath)

        audioUrl = publicUrlData.publicUrl
      }

      const { error: insertError } = await supabase.from('sales_rapportages').insert([{
        user_id: user.id,
        user_name: user.name,
        title: rapTitle.trim() || '',
        audio_url: audioUrl || null,
        duration_seconds: inputMode === 'record' ? recorder.duration : 0,
        notes: rapNotes.trim() || '',
        transcription: rapTranscription.trim() || '',
        ai_summary: null,
        client_name: rapClientName.trim() || '',
      }])

      if (insertError) throw insertError

      handleResetRecording()
      setSuccessMsg('Rapportage opgeslagen!')
      setTimeout(() => setSuccessMsg(''), 3000)
      loadRapportages()
    } catch (err) {
      console.error('Error saving rapportage:', err)
      setSaveError('Opslaan mislukt. Controleer verbinding en probeer opnieuw.')
    } finally {
      setSaving(false)
    }
  }

  // ─── Rapportage: delete ─────────────────────────────────────────

  const deleteRapportage = async (rap: Rapportage) => {
    try {
      if (rap.audio_url) {
        const url = new URL(rap.audio_url)
        const pathMatch = url.pathname.match(/sales-audio\/(.+)$/)
        if (pathMatch) {
          await supabase.storage.from('sales-audio').remove([pathMatch[1]])
        }
      }
      const { error } = await supabase.from('sales_rapportages').delete().eq('id', rap.id)
      if (!error) {
        if (playingId === rap.id) {
          setPlayingId(null)
        }
        if (expandedRapId === rap.id) {
          setExpandedRapId(null)
        }
        loadRapportages()
      }
    } catch (err) {
      console.error('Error deleting rapportage:', err)
    }
  }

  // ─── Rapportage: save edit ─────────────────────────────────────

  const openEdit = (rap: Rapportage) => {
    const s = rap.ai_summary as Record<string, any> | null | undefined
    setEditingRap(rap)
    setEditForm({ title: rap.title || '', client_name: rap.client_name || '', notes: s?.samenvatting || rap.notes || '' })
  }

  const saveEdit = async () => {
    if (!editingRap) return
    setEditSaving(true)
    try {
      const existing = editingRap.ai_summary as Record<string, any> | null | undefined
      const newSummary = existing ? { ...existing, samenvatting: editForm.notes } : null
      const { error } = await supabase.from('sales_rapportages').update({
        title: editForm.title,
        client_name: editForm.client_name,
        notes: editForm.notes,
        ...(newSummary ? { ai_summary: newSummary } : {}),
      }).eq('id', editingRap.id)
      if (!error) {
        setEditingRap(null)
        loadRapportages()
      }
    } finally {
      setEditSaving(false)
    }
  }

  // ─── Rapportage: play inline ────────────────────────────────────

  const togglePlay = (rap: Rapportage) => {
    if (!rap.audio_url) return

    if (playingId === rap.id) {
      audioPlayerRef.current?.pause()
      setPlayingId(null)
    } else {
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause()
      }
      const audio = new Audio(rap.audio_url)
      audioPlayerRef.current = audio
      audio.onended = () => setPlayingId(null)
      audio.play()
      setPlayingId(rap.id)
    }
  }

  // ─── Derived values ─────────────────────────────────────────────

  const handleLogout = () => { logout(); navigate('/login') }
  const time = now.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  const date = now.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' })
  const weekNumber = getISOWeekNumber(now)
  const pendingTasks = tasks.filter(t => !t.completed)
  const completedTasks = tasks.filter(t => t.completed)

  // ─── Filtered rapportages ──────────────────────────────────────

  const filteredRapportages = rapportages.filter(rap => {
    const summary = rap.ai_summary as Record<string, any> | null | undefined
    if (rapFilterStemming !== 'all' && summary?.stemming !== rapFilterStemming) return false
    if (rapSearch) {
      const q = rapSearch.toLowerCase()
      return (rap.title || '').toLowerCase().includes(q) || (rap.client_name || '').toLowerCase().includes(q)
    }
    return true
  })

  // ─── Shared: form fields (transcription + manual input) ───────────

  const renderFormFields = () => (
    <div className="w-full">
      {/* Whisper status */}
      {transcribing && (
        <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm text-blue-800 dark:text-blue-300 mb-3">
          <Loader2 size={15} className="animate-spin flex-shrink-0" />
          Transcriberen met Whisper...
        </div>
      )}
      {transcribeError && (
        <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-sm text-yellow-800 dark:text-yellow-300 mb-3">
          Whisper: {transcribeError} — Je kunt de tekst ook handmatig invullen.
        </div>
      )}

      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
        Transcriptie / Notities
      </label>
      <textarea
        value={rapTranscription}
        onChange={(e) => setRapTranscription(e.target.value)}
        placeholder="Transcriptie van je opname of typ hier het gesprek..."
        rows={4}
        className="input-field mb-3 resize-none text-sm"
      />

      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Titel</label>
      <input
        type="text"
        value={rapTitle}
        onChange={(e) => setRapTitle(e.target.value)}
        placeholder="Titel (optioneel)"
        className="input-field mb-2"
      />

      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Klantnaam</label>
      <input
        type="text"
        value={rapClientName}
        onChange={(e) => setRapClientName(e.target.value)}
        placeholder="Klantnaam (optioneel)"
        className="input-field mb-2"
      />

      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Notities / Samenvatting</label>
      <textarea
        value={rapNotes}
        onChange={(e) => setRapNotes(e.target.value)}
        placeholder="Aanvullende notities..."
        rows={3}
        className="input-field mb-3 resize-none"
      />

      {saveError && (
        <div className="p-3 bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300 rounded-lg text-sm mb-3">
          {saveError}
        </div>
      )}

      <div className="flex gap-2">
        <button onClick={handleResetRecording} className="btn-secondary flex-1 min-h-[44px]">Weggooien</button>
        <button onClick={saveRapportage} disabled={saving} className="btn-primary flex-1 min-h-[44px] disabled:opacity-50">
          {saving ? 'Opslaan...' : 'Opslaan'}
        </button>
      </div>
    </div>
  )

  // ─── Render ─────────────────────────────────────────────────────

  return (
    <div className="flex flex-col items-center min-h-screen overflow-x-hidden" style={{ background: 'var(--bg)' }}>
      <Header title="Sales Dashboard" onLogout={handleLogout} />

      <div className="w-full max-w-6xl px-3 sm:px-6 mt-6 sm:mt-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <p className="eyebrow">Persoonlijke werkruimte</p>
            <h2 className="page-title mt-2">Goedendag, {user?.name?.split(' ')[0] || 'collega'}</h2>
            <p className="page-subtitle mt-1">Beheer je taken en leg klantgesprekken direct vast.</p>
          </div>
          <div className="date-chip !items-end !flex-col !gap-0 text-right">
            <p className="text-base font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>{time}</p>
            <p className="text-[11px]">{date} · week {weekNumber}</p>
          </div>
        </div>
      </div>

      {/* Two-column grid: Tasks + Recorder */}
      <div className="w-full max-w-6xl px-3 sm:px-6 mt-5 sm:mt-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">

        {/* ═══ LEFT COLUMN: TAKEN ═══ */}
        <section className="card p-4 sm:p-6 flex flex-col">
          <div className="flex justify-between items-center border-b pb-3" style={{ borderColor: 'var(--border)' }}>
            <h2 className="text-base sm:text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              Mijn Taken ({pendingTasks.length} open)
            </h2>
            <button
              onClick={() => setShowTaskForm(!showTaskForm)}
              className="btn-primary !px-3"
            >
              <Plus size={16} />
              Taak
            </button>
          </div>

          {/* Add task form */}
          {showTaskForm && (
            <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <input
                type="text"
                value={newTask}
                onChange={(e) => setNewTask(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addTask()}
                placeholder="Nieuwe taak invoeren..."
                className="w-full p-3 text-lg border-2 border-[#920000] rounded mb-2 focus:outline-none dark:bg-gray-600 dark:text-white dark:border-red-700"
              />
              <input
                type="time"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                className="w-full p-2 border rounded text-lg mb-2 dark:bg-gray-600 dark:text-white dark:border-gray-500"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowTaskForm(false); setNewTask(''); setScheduledTime('') }}
                  className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600 min-h-[44px]"
                >
                  Annuleren
                </button>
                <button
                  onClick={addTask}
                  className="flex-1 bg-[#920000] text-white py-2 rounded-lg hover:bg-red-700 min-h-[44px]"
                >
                  Toevoegen
                </button>
              </div>
            </div>
          )}

          {/* Pending tasks */}
          <div className="mt-4 space-y-2 flex-1">
            {pendingTasks.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <CheckCircle size={48} className="mx-auto mb-2 text-green-400" />
                <p>Alle taken voltooid!</p>
              </div>
            ) : (
              pendingTasks.map(task => (
                <div key={task.id} className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700 border-l-4 border-l-yellow-400 hover:shadow-md transition-all">
                  <div className="flex items-start gap-3">
                    <button
                      onClick={() => toggleTask(task.id, task.completed)}
                      className="text-gray-400 hover:text-green-500 mt-0.5 min-h-[44px] flex items-center"
                    >
                      <Circle size={24} />
                    </button>
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-gray-800 dark:text-gray-100">{task.task}</span>
                      {task.created_by !== user?.id && (
                        <span className="ml-2 text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full dark:bg-blue-900 dark:text-blue-300">
                          Van: {task.created_by_name}
                        </span>
                      )}
                      <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-3">
                        {task.scheduled_at && (
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            {new Date(task.scheduled_at).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                        <span>{new Date(task.created_at).toLocaleDateString('nl-NL')}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => deleteTask(task.id)}
                      className="text-gray-400 hover:text-red-500 min-h-[44px] flex items-center"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Completed tasks */}
          {completedTasks.length > 0 && (
            <details className="mt-4">
              <summary className="cursor-pointer text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 py-2">
                {completedTasks.length} voltooide {completedTasks.length === 1 ? 'taak' : 'taken'} tonen
              </summary>
              <div className="mt-2 space-y-2">
                {completedTasks.map(task => (
                  <div key={task.id} className="p-3 rounded-lg bg-green-50 dark:bg-green-900/30 border-l-4 border-l-green-400">
                    <div className="flex items-start gap-3">
                      <button
                        onClick={() => toggleTask(task.id, task.completed)}
                        className="text-green-500 mt-0.5 min-h-[44px] flex items-center"
                      >
                        <CheckCircle size={24} />
                      </button>
                      <div className="flex-1 min-w-0">
                        <span className="line-through text-gray-500 dark:text-gray-400">{task.task}</span>
                        <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          Voltooid: {task.completed_at ? new Date(task.completed_at).toLocaleString('nl-NL') : 'Onbekend'}
                        </div>
                      </div>
                      <button
                        onClick={() => deleteTask(task.id)}
                        className="text-gray-400 hover:text-red-500 min-h-[44px] flex items-center"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </section>

        {/* ═══ RIGHT COLUMN: RAPPORTAGES ═══ */}
        <section className="card p-4 sm:p-6 flex flex-col">
          <h2 className="text-base sm:text-lg font-semibold border-b pb-3" style={{ color: 'var(--text-primary)', borderColor: 'var(--border)' }}>
            Rapportages
          </h2>

          {/* Success message */}
          {successMsg && (
            <div className="mt-3 p-3 bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 rounded-lg text-center text-sm font-medium">
              {successMsg}
            </div>
          )}

          {/* Error messages */}
          {recorder.error && (
            <div className="mt-3 p-3 bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300 rounded-lg text-center text-sm">
              {recorder.error}
            </div>
          )}
          {speech.error && (
            <div className="mt-3 p-3 bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300 rounded-lg text-center text-sm">
              {speech.error}
            </div>
          )}

          {/* Recorder UI */}
          <div className="mt-4 flex flex-col items-center">

            {/* ── IDLE: mode selector + start button ── */}
            {recorder.status === 'idle' && (
              <>
                {/* Mode selector tabs */}
                <div className="flex w-full mb-4 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                  <button
                    onClick={() => handleSwitchMode('record')}
                    className={`flex-1 py-2 text-sm font-medium transition flex items-center justify-center gap-1.5 min-h-[44px] ${
                      inputMode === 'record'
                        ? 'bg-[#920000] text-white'
                        : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    <Mic size={15} />
                    Opnemen
                  </button>
                  <button
                    onClick={() => handleSwitchMode('type')}
                    className={`flex-1 py-2 text-sm font-medium transition flex items-center justify-center gap-1.5 min-h-[44px] ${
                      inputMode === 'type'
                        ? 'bg-[#920000] text-white'
                        : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    ✏️ Zelf typen
                  </button>
                </div>

                {inputMode === 'record' ? (
                  <>
                    <button
                      onClick={handleStartRecording}
                      className="w-16 h-16 rounded-full bg-[#920000] hover:bg-red-700 text-white flex items-center justify-center transition shadow-lg"
                    >
                      <Mic size={28} />
                    </button>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Tik om op te nemen</p>
                    {!speech.isSupported && (
                      <p className="mt-1 text-xs text-gray-400 dark:text-gray-500 text-center">
                        Je browser ondersteunt geen live transcriptie. Typ je notities handmatig na opname.
                      </p>
                    )}
                  </>
                ) : (
                  /* Type mode: show form fields directly without recording */
                  <div className="w-full">
                    {renderFormFields()}
                  </div>
                )}
              </>
            )}

            {/* ── RECORDING ── */}
            {recorder.status === 'recording' && (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-3 h-3 rounded-full bg-red-600 animate-pulse" />
                  <span className="text-red-600 dark:text-red-400 text-sm font-medium">Opname bezig...</span>
                </div>
                <p className="text-2xl font-mono tabular-nums text-gray-800 dark:text-gray-100 mb-3">
                  {formatDuration(recorder.duration)}
                </p>

                {/* Live transcription */}
                {speech.isSupported && (
                  <div
                    ref={transcriptScrollRef}
                    className="w-full max-h-32 overflow-y-auto bg-gray-50 dark:bg-gray-700 rounded-lg p-3 mb-3 text-sm"
                  >
                    {speech.transcript || speech.interimResult ? (
                      <>
                        <span className="text-gray-800 dark:text-gray-100">{speech.transcript}</span>
                        {speech.interimResult && (
                          <span className="text-gray-400 dark:text-gray-500"> {speech.interimResult}</span>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500 italic">Wachten op spraak...</span>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <button
                    onClick={recorder.pauseRecording}
                    className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 flex items-center justify-center transition min-h-[44px]"
                  >
                    <Pause size={22} />
                  </button>
                  <button
                    onClick={handleStopRecording}
                    className="w-16 h-16 rounded-full bg-red-600 animate-pulse hover:bg-red-700 text-white flex items-center justify-center transition shadow-lg"
                  >
                    <Square size={24} />
                  </button>
                </div>
              </>
            )}

            {/* ── PAUSED ── */}
            {recorder.status === 'paused' && (
              <>
                <p className="text-sm text-yellow-600 dark:text-yellow-400 font-medium mb-2">Gepauzeerd</p>
                <p className="text-2xl font-mono tabular-nums text-gray-800 dark:text-gray-100 mb-3">
                  {formatDuration(recorder.duration)}
                </p>

                {/* Transcription so far */}
                {speech.isSupported && speech.transcript && (
                  <div
                    ref={transcriptScrollRef}
                    className="w-full max-h-32 overflow-y-auto bg-gray-50 dark:bg-gray-700 rounded-lg p-3 mb-3 text-sm"
                  >
                    <span className="text-gray-800 dark:text-gray-100">{speech.transcript}</span>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <button
                    onClick={recorder.resumeRecording}
                    className="w-14 h-14 rounded-full bg-[#920000] hover:bg-red-700 text-white flex items-center justify-center transition shadow-lg min-h-[44px]"
                  >
                    <Play size={24} />
                  </button>
                  <button
                    onClick={handleStopRecording}
                    className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 flex items-center justify-center transition min-h-[44px]"
                  >
                    <Square size={20} />
                  </button>
                </div>
              </>
            )}

            {/* ── STOPPED (review + AI) ── */}
            {recorder.status === 'stopped' && (
              <div className="w-full">
                <p className="text-center text-sm text-gray-500 dark:text-gray-400 mb-2">
                  Duur: {formatDuration(recorder.duration)}
                </p>
                {recorder.audioUrl && (
                  <audio controls src={recorder.audioUrl} className="w-full mb-3" />
                )}
                {renderFormFields()}
              </div>
            )}
          </div>

          {/* Previous rapportages list */}
          <div className="mt-6 flex-1">
            <div className="flex flex-col gap-2 mb-3">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={rapSearch}
                  onChange={e => setRapSearch(e.target.value)}
                  placeholder="Zoek op klant of titel..."
                  className="input-field pl-8 text-sm py-1.5"
                />
              </div>
              <div className="flex gap-1">
                {(['all', 'positief', 'neutraal', 'negatief'] as const).map(s => (
                  <button key={s} onClick={() => setRapFilterStemming(s)}
                    className={`flex-1 py-1 text-xs rounded-lg font-medium transition-colors ${
                      rapFilterStemming === s
                        ? s === 'positief' ? 'bg-green-600 text-white'
                          : s === 'negatief' ? 'bg-red-600 text-white'
                          : s === 'neutraal' ? 'bg-gray-500 text-white'
                          : 'bg-[#920000] text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                    }`}>
                    {s === 'all' ? 'Alle' : s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            {filteredRapportages.length === 0 ? (
              <p className="text-center text-sm text-gray-400 dark:text-gray-500 py-4">
                {rapportages.length === 0 ? 'Nog geen rapportages opgenomen' : 'Geen resultaten'}
              </p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {filteredRapportages.map(rap => {
                  const summary = rap.ai_summary as Record<string, any> | null | undefined
                  const isExpanded = expandedRapId === rap.id
                  return (
                    <div key={rap.id} className="rounded-lg bg-gray-50 dark:bg-gray-700 overflow-hidden">
                      <div className="flex items-center gap-2 p-2">
                        {rap.audio_url ? (
                          <button
                            onClick={() => togglePlay(rap)}
                            className="w-9 h-9 rounded-full bg-[#920000] hover:bg-red-700 text-white flex items-center justify-center flex-shrink-0 transition min-h-[44px]"
                          >
                            {playingId === rap.id ? <Pause size={16} /> : <Play size={16} />}
                          </button>
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center flex-shrink-0">
                            <Mic size={14} className="text-gray-400 dark:text-gray-500" />
                          </div>
                        )}
                        <div
                          className="flex-1 min-w-0 cursor-pointer"
                          onClick={() => setExpandedRapId(isExpanded ? null : rap.id)}
                        >
                          <div className="flex items-center gap-1.5">
                            <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                              {rap.title || 'Zonder titel'}
                            </p>
                            {rap.client_name && (
                              <span className="text-xs px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full flex-shrink-0">
                                {rap.client_name}
                              </span>
                            )}
                            {summary?.stemming && <StemmingBadge stemming={summary.stemming} />}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(rap.created_at).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            {rap.duration_seconds > 0 && <> &middot; {formatDuration(rap.duration_seconds)}</>}
                          </p>
                        </div>
                        <button
                          onClick={() => setExpandedRapId(isExpanded ? null : rap.id)}
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 flex-shrink-0 min-h-[44px] flex items-center"
                        >
                          {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>
                        <button
                          onClick={() => openEdit(rap)}
                          className="text-gray-400 hover:text-blue-500 flex-shrink-0 min-h-[44px] flex items-center"
                          title="Bewerken"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => deleteRapportage(rap)}
                          className="text-gray-400 hover:text-red-500 flex-shrink-0 min-h-[44px] flex items-center"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>

                      {/* Expanded details */}
                      {isExpanded && (
                        <div className="px-3 pb-3 border-t border-gray-200 dark:border-gray-600 pt-2">
                          {summary?.samenvatting && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">Samenvatting</p>
                              <p className="text-sm text-gray-700 dark:text-gray-200">{summary.samenvatting}</p>
                            </div>
                          )}
                          {summary?.besproken_onderwerpen && summary.besproken_onderwerpen.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">Besproken</p>
                              <ul className="text-sm text-gray-700 dark:text-gray-200 list-disc list-inside">
                                {summary.besproken_onderwerpen.map((item, i) => (
                                  <li key={i}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {summary?.afspraken && summary.afspraken.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">Afspraken</p>
                              <ul className="text-sm text-gray-700 dark:text-gray-200 list-disc list-inside">
                                {summary.afspraken.map((item, i) => (
                                  <li key={i}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {rap.notes && !summary?.samenvatting && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">Notities</p>
                              <p className="text-sm text-gray-700 dark:text-gray-200">{rap.notes}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </section>
      </div>
      {/* Edit rapportage modal */}
      {editingRap && (
        <div className="modal-overlay" onClick={() => setEditingRap(null)}>
          <div className="modal-content max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Rapportage bewerken</h3>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Titel</label>
            <input className="input-field mb-3" value={editForm.title} onChange={e => setEditForm(p => ({ ...p, title: e.target.value }))} placeholder="Titel" />
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Klantnaam</label>
            <input className="input-field mb-3" value={editForm.client_name} onChange={e => setEditForm(p => ({ ...p, client_name: e.target.value }))} placeholder="Klantnaam" />
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Samenvatting / Notities</label>
            <textarea className="input-field mb-4 resize-none" rows={4} value={editForm.notes} onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))} placeholder="Samenvatting" />
            <div className="flex gap-2">
              <button onClick={() => setEditingRap(null)} className="btn-secondary flex-1">Annuleren</button>
              <button onClick={saveEdit} disabled={editSaving} className="btn-primary flex-1 disabled:opacity-50">
                {editSaving ? 'Opslaan...' : 'Opslaan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
